import { useNavigate } from '@fukuri-admins/hooks/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { useGetBranchList } from '~/usecases/useGetBranchList';
import { useGetStressAssessmentSummary } from '~/usecases/useGetStressAssessmentSummary';
import { ROUTES } from '~/utils/routes';
import { useGetStressAssessmentBranchLinkageStatus } from './useGetStressAssessmentBranchLinkageStatus';

const SKIP_BRANCH_SETUP_DIALOG_KEY = 'stress_check_skip_branch_setup_dialog';

type Args = {
  stressAssessmentId: string;
};
type IUseStressAssessmentSummary = (args: Args) => {
  state: {
    data: ReturnType<typeof useGetStressAssessmentSummary>['data'];
    isLoading: boolean;
  };
  unsetBranchUserCount: number;
  onClickReportGuide: () => void;
  isDialogOpen: boolean;
  isDontShowAgainChecked: boolean;
  onConfirmDialog: () => void;
  onCancelDialog: () => void;
  onCloseDialog: () => void;
  onChangeDontShowAgain: () => void;
  isLinkageDialogOpen: boolean;
  onConfirmLinkageDialog: () => void;
  onCancelLinkageDialog: () => void;
};

export const useStressAssessmentSummary: IUseStressAssessmentSummary = ({ stressAssessmentId }) => {
  const threeLetterCode = getThreeLetterCode();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDontShowAgainChecked, setIsDontShowAgainChecked] = useState(false);
  const [isLinkageDialogOpen, setIsLinkageDialogOpen] = useState(false);

  const { data, isLoading } = useGetStressAssessmentSummary(threeLetterCode, stressAssessmentId);
  const { data: branchLinkageData } = useGetStressAssessmentBranchLinkageStatus(
    threeLetterCode,
    Number(stressAssessmentId)
  );
  const { totalCount: branchTotalCount } = useGetBranchList({ threeLetterCode, page: '1' });

  const unsetBranchUserCount =
    branchLinkageData?.stress_assessment_branch_linkage_status?.unset_branch_user_count ?? 0;

  const reportDownloadUrl = ROUTES.STRESS_ASSESSMENTS.REPORTS_DOWNLOAD.replace(
    ':id',
    stressAssessmentId
  );

  const saveDontShowAgainIfChecked = () => {
    if (isDontShowAgainChecked) {
      localStorage.setItem(SKIP_BRANCH_SETUP_DIALOG_KEY, 'true');
    }
  };

  const onClickReportGuide = () => {
    const isSkipped = localStorage.getItem(SKIP_BRANCH_SETUP_DIALOG_KEY) === 'true';
    if (branchTotalCount === 0 && !isSkipped) {
      setIsDialogOpen(true);
      return;
    }
    if (branchTotalCount > 0 && unsetBranchUserCount > 0) {
      setIsLinkageDialogOpen(true);
      return;
    }
    navigate(reportDownloadUrl);
  };

  const onConfirmDialog = () => {
    saveDontShowAgainIfChecked();
    navigate(reportDownloadUrl);
  };

  const onCancelDialog = () => {
    saveDontShowAgainIfChecked();
    navigate(ROUTES.BRANCHES.INDEX);
  };

  const onCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const onChangeDontShowAgain = () => {
    setIsDontShowAgainChecked(prev => !prev);
  };

  const onConfirmLinkageDialog = () => {
    setIsLinkageDialogOpen(false);
    navigate(`${ROUTES.USERS}?unsetBranchUserCount=${unsetBranchUserCount}`);
  };

  const onCancelLinkageDialog = () => {
    setIsLinkageDialogOpen(false);
  };

  return {
    state: {
      data,
      isLoading,
    },
    unsetBranchUserCount,
    onClickReportGuide,
    isDialogOpen,
    isDontShowAgainChecked,
    onConfirmDialog,
    onCancelDialog,
    onCloseDialog,
    onChangeDontShowAgain,
    isLinkageDialogOpen,
    onConfirmLinkageDialog,
    onCancelLinkageDialog,
  };
};
